import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import cn from 'clsx';
import {PATHS} from 'routes/constants';
import {APP_PAGES} from 'utils/pages';
import {shouldHideButton} from './utils';
import {usePath, usePaymentSettings} from 'hooks';
import {useSummary} from 'context/summary';
import {useBuildPaymentItem} from 'pages/Checkout/hooks/useBuildPaymentItem';
import {useCheckoutSettings} from '../../hooks/useCheckoutSettings';
import {PaymentItem} from './PaymentItem';
import {MainLoader} from '@chekin/guestapp-ui-kit';
import {PayButton} from './PayButton';
import {PaymentTotals} from './PaymentTotals';
import styles from './styles.module.css';

function PaymentSummaryBox() {
  const {t} = useTranslation('check-out');
  const {getFullPath} = usePath();
  const {currencySymbol} = usePaymentSettings();
  const {data: summary} = useSummary();
  const isBookingPaymentsActive = summary?.booking_payments?.active;

  const navigate = useNavigate();
  const {checkoutFlowStatus, isSurveyFlowEnabled} = useCheckoutSettings();

  const {
    isLoading: isLoadingPaymentItems,
    isPaymentsCartEmpty,
    paymentsCart,
    totalToPay,
    totalFee,
    items,
    isTotalAmountToPayZero,
  } = useBuildPaymentItem();

  const isSurveyFlow = checkoutFlowStatus !== 'FEEDBACK_SUBMITTED' && isSurveyFlowEnabled;

  const hideButton = shouldHideButton(
    checkoutFlowStatus,
    isSurveyFlowEnabled,
    isTotalAmountToPayZero,
  );

  const buttonLabel =
    isSurveyFlow && isTotalAmountToPayZero ? t('continue_to_survey') : t('pay_now');

  const redirectAfterPaid = useCallback(
    (to: string) => {
      navigate(getFullPath(PATHS.payments.form), {
        state: {
          payments: paymentsCart?.pay_now_payments,
          pay_later_payments: paymentsCart?.pay_later_payments,
          total_amount_to_pay: paymentsCart?.total_amount_to_pay,
          from: APP_PAGES.checkout,
          redirectAfterPaidPath: getFullPath(to),
        },
      });
    },
    [
      navigate,
      getFullPath,
      paymentsCart?.pay_now_payments,
      paymentsCart?.pay_later_payments,
      paymentsCart?.total_amount_to_pay,
    ],
  );

  const handleContinueFlow = useCallback(() => {
    if (isSurveyFlow && isPaymentsCartEmpty) {
      navigate(getFullPath(PATHS.checkout.rateYourStay));
      return;
    }

    if (isTotalAmountToPayZero) return;
    const redirectToSurvery =
      isSurveyFlowEnabled && checkoutFlowStatus !== 'FEEDBACK_SUBMITTED';

    const redirectAfterPaidPath = redirectToSurvery
      ? PATHS.checkout.rateYourStay
      : PATHS.checkout.thanks;

    redirectAfterPaid(redirectAfterPaidPath);
  }, [
    navigate,
    redirectAfterPaid,
    isTotalAmountToPayZero,
    getFullPath,
    isSurveyFlow,
    isSurveyFlowEnabled,
    checkoutFlowStatus,
    isPaymentsCartEmpty,
  ]);

  return (
    <>
      <div
        className={cn('flex flex-col gap-6 w-full checkout-summary-box', {
          'opacity-50 transition-all duration-500 ease-in-out pointer-events-none':
            isLoadingPaymentItems,
        })}
      >
        <div
          className={cn(
            styles.paymentBox__title,
            'block md:hidden checkout-summary-box__title',
          )}
        >
          {t('payment_summary')}
        </div>
        <div
          className={cn(styles.paymentBox__container, 'checkout-summary-box__container')}
        >
          <div
            className={cn(
              styles.paymentBox__title,
              'hidden md:block checkout-summary-box__title-desktop',
            )}
          >
            {t('payment_summary')}
          </div>
          {isLoadingPaymentItems && <MainLoader visible width={60} />}
          {!isLoadingPaymentItems &&
            items?.map(item => {
              return (
                <PaymentItem
                  key={item.id}
                  title={item.name}
                  price={item.amount}
                  currencySymbol={currencySymbol}
                />
              );
            })}

          <PaymentTotals
            className="hidden md:block"
            total={paymentsCart?.total_amount_to_pay || '0'}
            fee={`${currencySymbol}${totalFee}`}
            alreadyPaid={paymentsCart?.outside_paid_amount}
            currencySymbol={currencySymbol}
            totalPaid={totalToPay}
            isBookingPaymentsActive={isBookingPaymentsActive}
            button={
              <PayButton
                disabled={isLoadingPaymentItems}
                onClick={handleContinueFlow}
                label={buttonLabel}
                hidden={hideButton}
              />
            }
          />
        </div>
      </div>
      <PaymentTotals
        className="block md:hidden mobile-payment-totals"
        total={paymentsCart?.total_amount_to_pay || '0'}
        fee={`${currencySymbol}${totalFee}`}
        alreadyPaid={paymentsCart?.outside_paid_amount}
        currencySymbol={currencySymbol}
        totalPaid={totalToPay}
        isBookingPaymentsActive={isBookingPaymentsActive}
        button={
          <PayButton
            disabled={isLoadingPaymentItems}
            onClick={handleContinueFlow}
            label={buttonLabel}
            hidden={hideButton}
          />
        }
      />
    </>
  );
}

export {PaymentSummaryBox};
